import { useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { takeWizardHandoff } from '../../../Services/OrderWizard/handoff.ts'
import { trackWizardEntered } from '../../../Services/OrderWizard/telemetry.ts'
import { recordWholeSession } from '../../../Services/Telemetry/Replay.ts'
import { dropFiles } from '../../../Services/OrderWizard/dropFiles.ts'
import { defaultDraftName } from '../../../Services/OrderWizard/wizardFiles.ts'

const isFresh = (draft: DraftState): boolean =>
  !draft.orderId && draft.fileOrder.length === 0 && !draft.sheet && draft.phase === 'start'

/**
 * Counts the visit, has the session replayed in full, and drops handed-over files, each once per mount. The handoff is taken at mount,
 * not once a draft is ready, so a visit left early cannot pass its files on to the next one; the ref
 * keeps StrictMode's second effect pass from taking or dropping again.
 */
export const useWizardEntry = (draft: DraftState | null | undefined): void => {
  const dispatch = useDispatch()
  const entry = useRef<{ files: File[] } | null>(null)

  useEffect(() => {
    if (entry.current) {
      return
    }
    const handoff = takeWizardHandoff()
    entry.current = { files: handoff?.files ?? [] }
    trackWizardEntered(handoff?.source ?? 'direct', handoff?.via)
    recordWholeSession()
  }, [])

  useEffect(() => {
    const files = entry.current?.files
    if (!draft || !files?.length) {
      return
    }
    entry.current = { files: [] }
    let draftId = draft.id
    if (!isFresh(draft)) {
      draftId = uuidv4()
      dispatch(wizard.draftCreated({ draftId, name: defaultDraftName(new Date()), createdAt: new Date().toISOString() }))
    }
    dropFiles(draftId, files, dispatch)
  }, [draft, dispatch])
}
